/**
 * @function mockMatchMedia
 * @description Mock window.matchMedia for testing. Specifically needed for testing `useMobileDetector`.
 *  This is needed because react-testing-library doesn't render with `window` and `useMobileDetector` uses window.matchMedia to determine
 *  whether or not to render the component in a mobile or desktop environment.
 */
export const mockMatchMedia = () => {
    Object.defineProperty(window, 'matchMedia', {
        writable: true,
        value: jest.fn().mockImplementation((query) => ({
            matches: false,
            media: query,
            onchange: null,
            addListener: jest.fn(),
            removeListener: jest.fn(),
            addEventListener: jest.fn(),
            removeEventListener: jest.fn(),
            dispatchEvent: jest.fn(),
        })),
    });
};

/**
 * @function testID
 * @description Returns the past in id as a `data-testid` attribute. Could be extended in the future to provide more testing options/flexibility.
 * @param {String} id Value to use as the `data-testid` attribute
 * @param {String} [suffix = ''] Optional suffix to append to the id. Useful when creating test ids for elements that are repeated in a map
 *      @example testID('my-id', '-1') => data-testid="my-id-1"
 * @returns {Object} object with `data-testid` attribute
 */
export function testID(id, suffix = '') {
    return { 'data-testid': `${id}${suffix}` };
}

/**
 * @function testIdSetup
 * @description Creates a currying function that returns a function to apply a suffix to a common id. Ultimately returns the past in id and suffix as a `data-testid` attribute.
 * @param {String} id Value to use as the `data-testid` attribute
 * @param {String} suffix Value to append to the id. Useful when creating test ids for elements that are repeated in a map
 *      @example testIdSetup('my-id')('-1') => data-testid="my-id-1"
 *      @example const myId = testIdSetup('my-id');
 *              const myA = myId('-A') => data-testid="my-id-A"
 *              const myB = myId('-B') => data-testid="my-id-B"
 * @returns {Object} object with `data-testid` attribute
 */
export function testIdSetup(id) {
    return function innerTestId(suffix) {
        return { 'data-testid': `${id}${suffix}` };
    };
}

export default {
    testID,
};
